import type { FailCodeLocationEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';
import { toFailCodeLocationEntity } from '../helpers';

/**
 * Parameters for fetching all available fail code locations.
 */
export type FetchLocationsParams = {
    /** Optional abort signal to cancel the request. */
    signal?: AbortSignal;
};

/**
 * Fetch all available fail code locations. Locations are the first-level
 * selection in the no-trouble-found (NTF) cascade used by both QMRs and
 * Techline cases.
 */
export class FetchLocations extends StsBackendOperation {
    /**
     * Fetch locations from the API and map them to domain entities. Uses
     * {@link toFailCodeLocationEntity} because the API shape differs from the entity
     * (the `specific` field is renamed to `isSpecific`).
     *
     * @param params - optional abort signal
     * @returns the location entities
     */
    async exec(params: FetchLocationsParams = {}): Promise<FailCodeLocationEntity[]> {
        const { signal } = params;

        // Fetch locations from the backend
        const response = await this.client.fetchFailCodeLocations(signal);

        if (!response.success) {
            return [];
        }

        const locations = response.data.locations;

        if (!locations?.length) {
            return [];
        }

        // Map DTOs to domain entities (rename `specific` -> `isSpecific`)
        return locations.map(toFailCodeLocationEntity);
    }
}
